import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { AlertIcon, DragHandle6DotIcon } from "../../../../icons";
import { Button } from "../../../general/Button/Button";
import { TransactionRuleConditions } from "../../../transactionRules/TransactionRuleConditions";
import { TransactionRuleActions } from "../../../transactionRules/TransactionRuleActions";
import { ManagementDropdown } from "../../../general/ManagementDropdown/ManagementDropdown";
import { Counterparty } from "../../../../common/types/counterparty";
import { TransactionRuleFormModal } from "../TransactionRuleFormModal";
import { EditRuleModal } from "../EditRuleModal";
import { getRuleTransactionsLink } from "../utils";
import styles from "../RulesTable.module.scss";
import { TransactionRule } from "../../../../common/types/transactionRule";
import { ConfirmationModal } from "../../../general/ConfirmationModal";
import {
    useRuleRemovalMutation,
    useUpdateRule,
} from "../../../../api/rules.api";

/**
 * RuleRow displays a single rule row, including drag handle and visual indicators.
 */
interface RuleRowProps {
    readonly rule: TransactionRule;
    readonly isOver: boolean;
    readonly listeners?: React.HTMLAttributes<HTMLElement>;
    readonly attributes: React.HTMLAttributes<HTMLElement>;
    readonly isGroupDragged: boolean;
    readonly counterparties: Counterparty[];
    readonly transactionCounts: Record<string, number>;
    readonly isUnreachable?: boolean;
    readonly isDuplicate?: boolean;
}

export const RuleRow: React.FC<RuleRowProps> = ({
    rule,
    isOver,
    listeners = {},
    attributes = {},
    isGroupDragged,
    counterparties,
    transactionCounts,
    isUnreachable,
    isDuplicate,
}) => {
    const [showEdit, setShowEdit] = useState(false);
    const transactionsLink = getRuleTransactionsLink(rule);
    const showTransactions = useCallback(() => {
        window.open(transactionsLink);
    }, [transactionsLink]);
    const updateRule = useUpdateRule();
    const [showDisable, setShowDisable] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    const removal = useRuleRemovalMutation(rule);

    return (
        <>
            <td className={styles.td} style={{ position: "relative" }}>
                <div className="d-flex gap-1">
                    {(!!isUnreachable || !!isDuplicate) && !rule.isDisabled && (
                        <OverlayTrigger
                            placement="auto-start"
                            overlay={
                                <Tooltip id="broken-rule-tooltip">
                                    {isDuplicate
                                        ? "This rule is a duplicate of another rule."
                                        : "This rule conflicts with another rule. To enable it work, move it to the top."}
                                </Tooltip>
                            }
                        >
                            <div>
                                <AlertIcon className="icon-color-yellow-500 mr-2" />
                            </div>
                        </OverlayTrigger>
                    )}
                    <div>
                        <TransactionRuleConditions
                            rule={rule}
                            counterparties={counterparties}
                        />
                    </div>
                </div>
                {isOver && !isGroupDragged && (
                    <>
                        <div
                            className={classNames(
                                styles.insertionIndicator,
                                styles.top,
                                styles.withDot,
                            )}
                        />
                        <div
                            className={classNames(
                                styles.insertionIndicatorDot,
                                styles.top,
                            )}
                        />
                    </>
                )}
            </td>
            <td className={styles.td} style={{ position: "relative" }}>
                <TransactionRuleActions
                    rule={rule}
                    counterparties={counterparties}
                />
                {isOver && !isGroupDragged && (
                    <div
                        className={classNames(
                            styles.insertionIndicator,
                            styles.top,
                        )}
                    />
                )}
            </td>
            <td className={styles.td} style={{ position: "relative" }}>
                <div className={styles.ruleRowFlex}>
                    <Button
                        variant="tertiary"
                        onClick={showTransactions}
                        className={styles.numberOfTransactions}
                    >
                        {transactionCounts[rule.id] ?? 0}
                    </Button>
                    <div className="d-flex gap-buttons">
                        <Button
                            size="sm"
                            icon
                            variant="tertiary"
                            tabIndex={-1}
                            aria-label="Drag rule"
                            className={styles.dragButton}
                            {...listeners}
                            {...attributes}
                        >
                            <DragHandle6DotIcon />
                        </Button>
                        <ManagementDropdown
                            id={`rule-${rule.id}`}
                            className={classNames(
                                styles.dragButton,
                                styles.managementDropdown,
                            )}
                        >
                            <Dropdown.Item onClick={() => setShowEdit(true)}>
                                Edit
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setShowDisable(true)}>
                                {rule.isDisabled ? "Enable" : "Disable"}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => setShowDelete(true)}
                                className={styles.deleteItem}
                            >
                                Delete
                            </Dropdown.Item>
                        </ManagementDropdown>
                    </div>
                </div>
                {isOver && !isGroupDragged && (
                    <div
                        className={classNames(
                            styles.insertionIndicator,
                            styles.top,
                        )}
                    />
                )}
                <TransactionRuleFormModal
                    show={showEdit}
                    onHide={() => setShowEdit(false)}
                >
                    <EditRuleModal
                        rule={rule}
                        close={() => setShowEdit(false)}
                        counterparties={counterparties}
                    />
                </TransactionRuleFormModal>

                <ConfirmationModal
                    show={showDelete}
                    onHide={() => setShowDelete(false)}
                    onConfirm={async () => {
                        await removal.mutateAsync();
                        setShowDelete(false);
                    }}
                    title={"Are you sure you want to delete this rule?"}
                    question={`This rule has affected ${transactionCounts[rule.id] ?? 0} transactions. After deleting, the rule will not be available for future transactions. For temporary disabling, use the disable option.`}
                    yes="Delete"
                    yesBtnVariant="danger"
                />

                <ConfirmationModal
                    show={showDisable}
                    onHide={() => setShowDisable(false)}
                    onConfirm={async () => {
                        await updateRule.mutateAsync({
                            workspaceId: rule.workspaceId,
                            ruleId: rule.id,
                            rule: {
                                ...rule,
                                isDisabled: !rule.isDisabled,
                            },
                            applyToExistingTransactions: false,
                        });
                        setShowDisable(false);
                    }}
                    title={
                        rule.isDisabled
                            ? "Are you sure you want to enable this rule?"
                            : "Are you sure you want to disable this rule?"
                    }
                    question={
                        rule.isDisabled
                            ? "After enabling, the rule will be applied to future transactions. You can disable it later."
                            : "After disabling, the rule will not be applied to future transactions. You can re-enable it later."
                    }
                    yes={rule.isDisabled ? "Enable" : "Disable"}
                    yesBtnVariant={rule.isDisabled ? "default" : "danger"}
                />
            </td>
        </>
    );
};
